import { NexusConfig } from '../config.js';

/**
 * Discovery Repository
 * Abstracts API calls for discovery and framework scoring operations
 */
class DiscoveryRepository {
    constructor(baseUrl = '') {
        this.baseUrl = baseUrl;
    }

    /**
     * Fetch with one re-auth retry on 401 (expired/invalid token).
     * Recomputes the Authorization header on every attempt so the retry after
     * a REQUEST_AUTH refresh picks up the fresh token from sessionStorage.
     * @param {string} url - Request URL
     * @param {Object} options - fetch options (headers merged with fresh auth)
     * @returns {Promise<Response>}
     */
    async _fetch(url, options = {}) {
        return NexusConfig.fetchWithAuthRetry(() => fetch(url, {
            ...options,
            headers: { ...(options.headers || {}), ...this._getAuthHeaders() }
        }));
    }

    /**
     * Get auth headers if AuthService is available
     * Priority: 1) NEXUS JWT (from postMessage), 2) AuthService
     * @returns {Object} Headers object with Authorization if authenticated
     */
    _getAuthHeaders() {
        // First: Check for NEXUS JWT stored via AUTH_TOKEN postMessage
        // This is set by NexusConfig._handleAuthToken() when embedded in NEXUS
        if (typeof sessionStorage !== 'undefined') {
            const nexusToken = sessionStorage.getItem('nexus_access_token');
            if (nexusToken) {
                console.log('[DiscoveryRepository] Using NEXUS JWT from sessionStorage');
                return { Authorization: `Bearer ${nexusToken}` };
            }
        }

        // Second: Try window.AuthService (set by auth.js)
        if (typeof window !== 'undefined' && window.AuthService) {
            const headers = window.AuthService.getAuthHeaders();
            if (headers.Authorization) {
                console.log('[DiscoveryRepository] Auth headers from AuthService');
                return headers;
            }
        }

        // Third: Try global AuthService
        if (typeof AuthService !== 'undefined') {
            const headers = AuthService.getAuthHeaders();
            if (headers.Authorization) {
                console.log('[DiscoveryRepository] Auth headers (global)');
                return headers;
            }
        }

        // No auth available - return empty headers, let server respond with 401
        console.warn('[DiscoveryRepository] No auth token available - request will be unauthenticated');
        return {};
    }

    /**
     * Merge auth headers with other headers
     * @param {Object} headers - Additional headers
     * @returns {Object} Merged headers
     */
    _mergeHeaders(headers = {}) {
        return { ...this._getAuthHeaders(), ...headers };
    }

    /**
     * Score frameworks based on discovery data
     * @param {Object} discoveryData - Discovery form data
     * @returns {Promise<Array>} Scored and ranked frameworks
     */
    async scoreFrameworks(discoveryData) {
        const response = await this._fetch(`${this.baseUrl}/api/score-frameworks`, {
            method: 'POST',
            headers: this._mergeHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(discoveryData)
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`SCORE_FRAMEWORKS_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * Get available frameworks
     * @returns {Promise<Array>} List of available frameworks
     */
    async getAvailableFrameworks() {
        const response = await this._fetch(`${this.baseUrl}/api/frameworks`, {
            headers: this._getAuthHeaders()
        });

        if (!response.ok) {
            throw new Error('Failed to fetch available frameworks');
        }

        return response.json();
    }

    /**
     * Save discovery data
     * @param {Object} discoveryData - Discovery form data
     * @returns {Promise<Object>} Save confirmation
     */
    async saveDiscoveryData(discoveryData) {
        const response = await this._fetch(`${this.baseUrl}/api/discovery/save`, {
            method: 'POST',
            headers: this._mergeHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(discoveryData)
        });

        if (!response.ok) {
            throw new Error('Failed to save discovery data');
        }

        return response.json();
    }

    /**
     * Update existing campaign discovery data and regenerate all content
     * @param {string} campaignId - Campaign identifier
     * @param {Object} discoveryData - Updated discovery form data
     * @returns {Promise<Object>} Update result with version info
     */
    async updateDiscoveryData(campaignId, discoveryData) {
        const response = await this._fetch(`${this.baseUrl}/api/discovery/${campaignId}/save`, {
            method: 'POST',
            headers: this._mergeHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ discoveryData })
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`UPDATE_DISCOVERY_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * Save discovery draft to server without regenerating content
     * Used for partial saves during editing (Next/Previous button clicks)
     * @param {string} campaignId - Campaign identifier
     * @param {Object} discoveryData - Current discovery form data
     * @returns {Promise<Object>} Save confirmation
     */
    async saveDraftToServer(campaignId, discoveryData) {
        const response = await this._fetch(`${this.baseUrl}/api/discovery/${campaignId}/draft`, {
            method: 'PATCH',
            headers: this._mergeHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ discoveryData })
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`SAVE_DRAFT_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * List all versions for a campaign
     * @param {string} campaignId - Campaign identifier
     * @returns {Promise<Object>} Version metadata with history
     */
    async listVersions(campaignId) {
        const response = await this._fetch(`${this.baseUrl}/api/campaigns/${campaignId}/versions`, {
            headers: this._getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`LIST_VERSIONS_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * Get content from a specific version
     * @param {string} campaignId - Campaign identifier
     * @param {number} versionNum - Version number
     * @returns {Promise<Object>} Version content
     */
    async getVersion(campaignId, versionNum) {
        const response = await this._fetch(`${this.baseUrl}/api/campaigns/${campaignId}/versions/${versionNum}`, {
            headers: this._getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`GET_VERSION_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * Get discovery data for a campaign
     * @param {string} campaignId - Campaign identifier
     * @returns {Promise<Object>} Discovery data
     */
    async getDiscoveryData(campaignId) {
        const response = await this._fetch(`${this.baseUrl}/api/discovery/${campaignId}`, {
            headers: this._getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`GET_DISCOVERY_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * Get product data by product ID
     * @param {string} productId - Product identifier
     * @returns {Promise<Object>} Product data
     */
    async getProductData(productId) {
        const response = await this._fetch(`${this.baseUrl}/product/${productId}`, {
            headers: this._getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`GET_PRODUCT_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * Get combined product and business analysis data
     * @param {string} productId - Product identifier
     * @returns {Promise<Object>} Combined product + business data
     */
    async getProductKnowledge(productId) {
        const response = await this._fetch(`${this.baseUrl}/product-knowledge/${productId}`, {
            headers: this._getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`GET_PRODUCT_KNOWLEDGE_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    // ============================================
    // INCOMPLETE CAMPAIGNS MANAGEMENT METHODS
    // ============================================

    /**
     * Save campaign draft to server
     * Creates campaign directory and campaign.json with status='draft'
     * @param {Object} discoveryData - Current discovery form data
     * @returns {Promise<Object>} { campaignId, completionPercent, message }
     */
    async saveCampaignDraft(discoveryData, nexusId = null) {
        // Ensure brandName is set (backend requires it, form uses productName)
        const dataToSave = { ...discoveryData };
        if (!dataToSave.brandName && dataToSave.productName) {
            dataToSave.brandName = dataToSave.productName;
        }

        // Build request body - include nexusId if provided for PostgreSQL linking
        const requestBody = { discoveryData: dataToSave };
        if (nexusId) {
            requestBody.nexusId = nexusId;
        }

        const response = await this._fetch(`${this.baseUrl}/api/campaign-draft`, {
            method: 'POST',
            headers: this._mergeHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`SAVE_CAMPAIGN_DRAFT_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * Get all incomplete campaigns
     * Returns campaigns grouped by age (recent vs older)
     * @returns {Promise<Object>} { recent: [], older: [], totalCount: N }
     */
    async getIncompleteCampaigns() {
        const response = await this._fetch(`${this.baseUrl}/api/incomplete-campaigns`, {
            headers: this._getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`GET_INCOMPLETE_CAMPAIGNS_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * Delete campaign draft
     * @param {string} campaignId - Campaign ID to delete
     * @returns {Promise<Object>} { message: string }
     */
    async deleteCampaignDraft(campaignId) {
        const response = await this._fetch(`${this.baseUrl}/api/campaign-draft/${campaignId}`, {
            method: 'DELETE',
            headers: this._getAuthHeaders()
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`DELETE_DRAFT_FAILED: ${errorMessage}`);
        }

        return response.json();
    }

    /**
     * Bulk delete campaign drafts
     * @param {Array<string>} campaignIds - Array of campaign IDs to delete
     * @returns {Promise<Object>} { deletedCount: N, message: string }
     */
    async bulkDeleteDrafts(campaignIds) {
        const response = await this._fetch(`${this.baseUrl}/api/campaign-drafts/bulk`, {
            method: 'DELETE',
            headers: this._mergeHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ campaignIds })
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error?.message || errorData.error || `HTTP ${response.status}`;
            } catch (parseError) {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
            throw new Error(`BULK_DELETE_FAILED: ${errorMessage}`);
        }

        return response.json();
    }
}

// ES6 export for browser modules
export { DiscoveryRepository };
